import React from 'react';
import { FullProduct } from 'screens/Applications&WorkOrders/Applications/Hooks/useApplication';
import formatNumber, {
  formatCurrency,
  formatPercentage
} from 'helpers/formatNumber';
import { getAbbreviatedUnit } from 'helpers/unitsAbbreviation';
import { Maths } from 'helpers/maths';
import OpenInNewIcon from 'assets/open_in_new.svg?react';
import { Button, ButtonType } from 'syngenta-digital-cropwise-react-ui-kit';
import formatRegistrationNumber from 'helpers/formatRegistrationNumber';
import { capitalizeName } from 'helpers/textHelpers';
import ProductTypeDisplay from 'screens/ProductList/helpers/ProductTypeDisplay';
import useInventoryDocumentationData from 'screens/ProductList/ProductDetails/hooks/useInventoryDocumentationData';
import { applicationMethodOptions } from 'screens/Applications&WorkOrders/StepsLayout/Constants/selectOptions';

// Constants
const EMPTY_VALUE = '---';
const BORDER_COLOR = '#DFE2E7';
const LABEL_COLOR = '#696F88';
const ICON_COLOR = '#14803C';

interface ContentProps {
  product: FullProduct;
  hasTankInformation: boolean;
}

interface DetailRowProps {
  label: string;
  value?: string | number | null;
  children?: React.ReactNode;
}

const DetailRow = ({ label, value, children }: DetailRowProps) => {
  return (
    <div
      className="flex pt-3 pb-2 border-b"
      style={{ borderColor: BORDER_COLOR }}
    >
      <span className="text-sm w-40 shrink-0" style={{ color: LABEL_COLOR }}>
        {label}
      </span>
      {children ? (
        <div className="text-sm text-neutral-90 font-normal flex-1 wrap-break-word">
          {children}
        </div>
      ) : (
        <span className="text-sm text-neutral-90 font-normal flex-1 wrap-break-word">
          {value || EMPTY_VALUE}
        </span>
      )}
    </div>
  );
};

const Content = ({ product, hasTankInformation }: ContentProps) => {
  const {
    isLoading: isLoadingDocument,
    openFile
  } = useInventoryDocumentationData(product.productId);

  const getApplicationMethodLabel = (method: string | undefined): string => {
    if (!method) return EMPTY_VALUE;

    // Convert UPPER_CASE to camelCase to match applicationMethodOptions format
    const camelCaseMethod = method
      .toLowerCase()
      .split('_')
      .map((word, index) =>
        index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)
      )
      .join('');

    const option = applicationMethodOptions.find(
      opt => opt.value === camelCaseMethod
    );
    return option ? option.label : EMPTY_VALUE;
  };

  const formatValue = (
    value: number | null | undefined,
    unit: string,
    suffix = '',
    decimals = 2
  ): string => {
    return value !== null && value !== undefined
      ? formatNumber(value, getAbbreviatedUnit(unit), decimals) + suffix
      : EMPTY_VALUE;
  };

  const formatPricePerUnit = (): string => {
    if (!product.priceValue || !product.priceUnit) {
      return EMPTY_VALUE;
    }
    return `${formatCurrency(product.priceValue)}/${getAbbreviatedUnit(
      product.priceUnit
    )}`;
  };

  // Check if standard package unit is different from standard unit
  const hasStandardPackageUnit =
    product.stdPackageUnit && product.stdPackageUnit !== product.stdUnit;

  return (
    <div className="mt-6">
      <DetailRow label="Product Type">
        <div className="flex items-center">
          <ProductTypeDisplay
            productType={capitalizeName(product.productType)}
            iconSize={18}
          />
        </div>
      </DetailRow>
      <DetailRow
        label="Standard Unit"
        value={capitalizeName(product.stdUnit)}
      />
      {hasStandardPackageUnit && (
        <DetailRow
          label="Standard Package Unit"
          value={capitalizeName(product.stdPackageUnit)}
        />
      )}
      <DetailRow
        label="EPA Number"
        value={formatRegistrationNumber(product.registrationNumber)}
      />
      <DetailRow
        label="Rate per Acre"
        value={formatValue(
          product.ratePerAreaValue,
          product.ratePerAreaUnit,
          '/ac'
        )}
      />
      {hasTankInformation && (
        <DetailRow
          label="Rate per Tank"
          value={formatValue(
            product.ratePerTankValue,
            product.ratePerTankUnit,
            '/tank'
          )}
        />
      )}
      <DetailRow
        label="Total Product"
        value={formatValue(product.totalProductValue, product.totalProductUnit)}
      />
      <DetailRow label="Applied Acres">
        <div>
          <div>
            {formatValue(product.appliedAreaValue, product.appliedAreaUnit)}
          </div>
          {!!product.appliedAreaCoveragePercent && (
            <div className="text-sm text-neutral-90 font-normal">
              {formatPercentage(
                Maths.divide(product.appliedAreaCoveragePercent, 100)
              )}
            </div>
          )}
        </div>
      </DetailRow>
      <DetailRow label="Price per Unit" value={formatPricePerUnit()} />
      <DetailRow
        label="Total Cost"
        value={
          product.totalCostValue
            ? formatCurrency(product.totalCostValue)
            : EMPTY_VALUE
        }
      />
      <DetailRow
        label="Application Method"
        value={getApplicationMethodLabel(product.applicationMethod)}
      />
      <DetailRow label="Target Pest">
        {product.targetPests && product.targetPests.length > 0 ? (
          <div className="wrap-break-word">
            {product.targetPests
              .map(pest => capitalizeName(pest.pestName))
              .join(', ')}
          </div>
        ) : (
          <span>{EMPTY_VALUE}</span>
        )}
      </DetailRow>

      {product.productLabelDocumentId && (
        <div className="w-full mt-6">
          <Button
            type={ButtonType.primary}
            ghost
            onClick={() => openFile(product.productLabelDocumentId)}
            icon={<OpenInNewIcon style={{ color: ICON_COLOR }} />}
            block
            className="product-label-button"
            loading={isLoadingDocument}
          >
            Open Product Label
          </Button>
        </div>
      )}
    </div>
  );
};

export default Content;
